import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  Button,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';

interface AboutUsContent {
  title: string;
  subtitle: string;
  missionTitle: string;
  missionContent: string;
  featuresTitle: string;
  features: {
    title: string;
    description: string;
    icon: string;
  }[];
  contactTitle: string;
  contactSubtitle: string;
  contactEmail: string;
}

// Default content (fallback)
const defaultContent: AboutUsContent = {
  title: 'About Goat Cards',
  subtitle: 'Your premier destination for premium sports cards and collectibles',
  missionTitle: 'Our Mission',
  missionContent: 'At Goat Cards, we\'re passionate about connecting collectors with the finest sports cards and memorabilia. Our platform combines cutting-edge technology with deep market insights to provide you with the most comprehensive and reliable card collecting experience.',
  featuresTitle: 'What We Offer',
  features: [
    {
      title: 'Live Market Data',
      description: 'Real-time pricing and market trends from eBay and other major platforms, ensuring you always have the most current information.',
      icon: 'TrendingUp'
    },
    {
      title: 'Secure Transactions',
      description: 'Safe and secure payment processing with buyer protection, ensuring your transactions are always protected.',
      icon: 'Security'
    },
    {
      title: 'Expert Support',
      description: 'Dedicated customer support team with deep knowledge of the sports card market to assist you with any questions.',
      icon: 'Support'
    }
  ],
  contactTitle: 'Get in Touch',
  contactSubtitle: 'Have questions about our platform or need assistance? We\'re here to help you build your perfect collection.',
  contactEmail: 'support@goatcards.shop'
};

const AboutUsPage: React.FC = () => {
  const { mode } = useCustomTheme();
  const [content, setContent] = useState<AboutUsContent>(defaultContent);
  
  // Determine colors based on theme mode
  const isLightMode = mode === 'light';
  const textColor = isLightMode ? '#000000' : '#FFFFFF';
  const secondaryTextColor = isLightMode ? '#4A5568' : '#CCCCCC';



  // Load content from localStorage on component mount and set up storage listener
  useEffect(() => {
    const loadContent = () => {
      const savedContent = localStorage.getItem('aboutUsContent');
      if (savedContent) {
        try {
          const parsedContent = JSON.parse(savedContent);
          setContent(parsedContent);
          console.log('About Us page: Loaded content from localStorage:', parsedContent);
        } catch (error) {
          console.error('Error loading saved content:', error);
          // If there's an error parsing, use default content
          setContent(defaultContent);
        }
      } else {
        console.log('About Us page: No saved content found, using default');
        setContent(defaultContent);
      }
    };

    // Load content immediately
    loadContent();

    // Set up storage event listener to update when localStorage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'aboutUsContent') {
        console.log('About Us page: localStorage changed, reloading content');
        loadContent();
      }
    };

    // Listen for storage events (when localStorage changes in other tabs/windows)
    window.addEventListener('storage', handleStorageChange);

    // Also set up a custom event listener for same-tab updates
    const handleCustomStorageChange = () => {
      console.log('About Us page: Custom storage event received, reloading content');
      loadContent();
    };

    window.addEventListener('aboutUsContentUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('aboutUsContentUpdated', handleCustomStorageChange);
    };
  }, []);

  // Helper function to get icon component
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'TrendingUp':
        return <TrendingUpIcon />;
      case 'Security':
        return <SecurityIcon />;
      case 'Support':
        return <SupportIcon />;
      default:
        return <TrendingUpIcon />;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>


      {/* Hero Section */}
      <Box sx={{ textAlign: 'center', mb: 8 }}>
        <Typography
          variant="h2"
          sx={{
            fontWeight: 800,
            mb: 3,
            color: textColor,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            letterSpacing: '-0.02em',
          }}
        >
          {content.title}
        </Typography>
        <Typography
          variant="h5"
          sx={{
            color: secondaryTextColor,
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.6,
          }}
        >
          {content.subtitle}
        </Typography>
      </Box>

      {/* Mission Section */}
      <Box sx={{ mb: 8 }}>
        <Paper
          elevation={2}
          sx={{
            p: 6,
            background: isLightMode ? '#f8f9fa' : '#1a1a1a',
            borderRadius: 3,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 4,
              color: textColor,
              textAlign: 'center',
            }}
          >
            {content.missionTitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontSize: '1.2rem',
              lineHeight: 1.8,
              color: secondaryTextColor,
              textAlign: 'center',
              maxWidth: 900,
              mx: 'auto',
            }}
          >
            {content.missionContent}
          </Typography>
        </Paper>
      </Box>

      {/* What We Offer Section */}
      <Box sx={{ mb: 8 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 700,
            mb: 5,
            color: textColor,
            textAlign: 'center',
          }}
        >
          {content.featuresTitle}
        </Typography>
        <Grid container spacing={4}>
          {content.features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  p: 4,
                  textAlign: 'center',
                  borderRadius: 3,
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mb: 3,
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: '50%',
                      backgroundColor: isLightMode ? '#000000' : '#FFFFFF',
                      color: isLightMode ? '#FFFFFF' : '#000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {getIconComponent(feature.icon)}
                  </Box>
                </Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    mb: 2,
                    color: textColor,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: secondaryTextColor,
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Contact Information */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: textColor,
          }}
        >
          {content.contactTitle}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: '1.1rem',
            color: secondaryTextColor,
            mb: 4,
          }}
        >
          {content.contactSubtitle}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: secondaryTextColor,
            mb: 6, // Increased margin-bottom to separate from buttons
          }}
        >
          Email: {content.contactEmail}
        </Typography>
        
        {/* Legal Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => window.open('/legal', '_blank')}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: '8px',
              borderWidth: 2,
              color: textColor,
              borderColor: textColor,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: isLightMode ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            Legal Information
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => window.open('/terms-privacy', '_blank')}
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: '8px',
              borderWidth: 2,
              color: textColor,
              borderColor: textColor,
              '&:hover': {
                borderWidth: 2,
                backgroundColor: isLightMode ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.08)',
              }
            }}
          >
            Terms & Privacy Policy
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

export default AboutUsPage; 